import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IHomeworkDashboardData,
  IHomeworkDashboardResponse,
  IHomeworkDashboardState,
} from '../../interface/homework.interface';
import {
  createHomework,
  editHomework,
  duplicateHomework,
  fetchHomeworkDashboard,
  getHomeworkById,
  publishHomework,
  startGrading,
  releaseTask,
  fetchTaskSubmissions,
  gradeTaskSubmission,
} from '../action/homework.action';

const initialData: IHomeworkDashboardData = {
  homeworks: [],
  analytics: {
    totalHomeworks: 0,
    drafts: 0,
    readyToPublish: 0,
    published: 0,
    grading: 0,
    scoreReleased: 0,
    cancelled: 0,
  },
  pagination: {
    total: 0,
    page: 1,
    pageSize: 5,
    totalPages: 0,
  },
};

const initialState: IHomeworkDashboardState = {
  ...initialData,
  loading: false,
  error: null,
  createLoading: false,
  createError: null,
  hydrateLoading: false,
  hydrateError: null,
  editLoading: false,
  editError: null,
  duplicateError: null,
  statusChangeLoading: false,
  statusChangeError: null,
  gradingLoading: false,
  gradingError: null,
  gradeSaveLoading: false,
  gradeSaveError: null,
};

const homeworkSlice = createSlice({
  name: 'homework',
  initialState,
  reducers: {
    resetHomeworkDashboard: () => initialState,
    clearHomeworkError: (state) => {
      state.error = null;
      state.createError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHomeworkDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchHomeworkDashboard.fulfilled,
        (state, action: PayloadAction<IHomeworkDashboardResponse>) => {
          state.loading = false;
          state.homeworks = action.payload.data.homeworks;
          state.analytics = action.payload.data.analytics;
          state.pagination = action.payload.data.pagination;
          state.error = null;
        },
      )
      .addCase(fetchHomeworkDashboard.rejected, (state, action) => {
        state.loading = false;
        state.homeworks = [];
        state.analytics = initialData.analytics;
        state.pagination = initialData.pagination;
        state.error = action.payload?.message || 'Failed to fetch homework.';
      })
      .addCase(createHomework.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createHomework.fulfilled, (state) => {
        state.createLoading = false;
        state.createError = null;
      })
      .addCase(createHomework.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload?.message || 'Failed to create homework.';
      })
      .addCase(getHomeworkById.pending, (state) => {
        state.hydrateLoading = true;
        state.hydrateError = null;
      })
      .addCase(getHomeworkById.fulfilled, (state) => {
        state.hydrateLoading = false;
        state.hydrateError = null;
      })
      .addCase(getHomeworkById.rejected, (state, action) => {
        state.hydrateLoading = false;
        state.hydrateError = action.payload?.message || 'Failed to load homework.';
      })
      .addCase(editHomework.pending, (state) => {
        state.editLoading = true;
        state.editError = null;
      })
      .addCase(editHomework.fulfilled, (state) => {
        state.editLoading = false;
        state.editError = null;
      })
      .addCase(editHomework.rejected, (state, action) => {
        state.editLoading = false;
        state.editError = action.payload?.message || 'Failed to update homework.';
      })
      // Duplicate-homework flags.
      .addCase(duplicateHomework.pending, (state) => {
        state.duplicateError = null;
      })
      .addCase(duplicateHomework.fulfilled, (state) => {
        state.duplicateError = null;
      })
      .addCase(duplicateHomework.rejected, (state, action) => {
        state.duplicateError = action.payload?.message || 'Failed to duplicate homework.';
      })
      // Publish status-transition flags.
      .addCase(publishHomework.pending, (state) => {
        state.statusChangeLoading = true;
        state.statusChangeError = null;
      })
      .addCase(publishHomework.fulfilled, (state) => {
        state.statusChangeLoading = false;
        state.statusChangeError = null;
      })
      .addCase(publishHomework.rejected, (state, action) => {
        state.statusChangeLoading = false;
        state.statusChangeError = action.payload?.message || 'Failed to publish homework.';
      })
      // Start grading + release task share the status-transition flags.
      .addCase(startGrading.pending, (state) => {
        state.statusChangeLoading = true;
        state.statusChangeError = null;
      })
      .addCase(startGrading.fulfilled, (state) => {
        state.statusChangeLoading = false;
      })
      .addCase(startGrading.rejected, (state, action) => {
        state.statusChangeLoading = false;
        state.statusChangeError = action.payload?.message || 'Failed to start grading.';
      })
      .addCase(releaseTask.pending, (state) => {
        state.statusChangeLoading = true;
        state.statusChangeError = null;
      })
      .addCase(releaseTask.fulfilled, (state) => {
        state.statusChangeLoading = false;
      })
      .addCase(releaseTask.rejected, (state, action) => {
        state.statusChangeLoading = false;
        state.statusChangeError = action.payload?.message || 'Failed to release scores.';
      })
      // Task submissions list.
      .addCase(fetchTaskSubmissions.pending, (state) => {
        state.gradingLoading = true;
        state.gradingError = null;
      })
      .addCase(fetchTaskSubmissions.fulfilled, (state) => {
        state.gradingLoading = false;
      })
      .addCase(fetchTaskSubmissions.rejected, (state, action) => {
        state.gradingLoading = false;
        state.gradingError = action.payload?.message || 'Failed to load submissions.';
      })
      // Saving a single mark.
      .addCase(gradeTaskSubmission.pending, (state) => {
        state.gradeSaveLoading = true;
        state.gradeSaveError = null;
      })
      .addCase(gradeTaskSubmission.fulfilled, (state) => {
        state.gradeSaveLoading = false;
      })
      .addCase(gradeTaskSubmission.rejected, (state, action) => {
        state.gradeSaveLoading = false;
        state.gradeSaveError = action.payload?.message || 'Failed to save mark.';
      });
  },
});

export const { resetHomeworkDashboard, clearHomeworkError } = homeworkSlice.actions;

export default homeworkSlice.reducer;
